import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthProvider';
import { FaUsers, FaComments, FaBrain, FaWhatsapp, FaEnvelope, FaHandshake, FaFlask, FaExclamationCircle } from 'react-icons/fa';
import axios from 'axios';

// ===================================================================================
// A. Skeleton Loader Component
// Provides a visually appealing loading state that matches the card's structure.
// ===================================================================================
const StatCardSkeleton = () => (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-5 animate-pulse">
        <div className="flex justify-between items-center mb-4">
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            <div className="h-6 w-6 bg-gray-700 rounded-md"></div>
        </div>
        <div className="h-10 bg-gray-700 rounded w-1/3"></div>
    </div>
);


// ===================================================================================
// B. Redesigned Stat Card Component
// A "glassmorphic" card with a colored top border and interactive hover effects.
// ===================================================================================
const StatCard = ({ icon, title, value, colorClasses }) => (
    <div className={`
        bg-gray-800/50 border border-gray-700 rounded-lg p-5
        transition-all duration-300 group hover:border-transparent
        relative overflow-hidden
        border-t-4 ${colorClasses.border}
    `}>
        {/* Glow effect on hover */}
        <div className={`absolute -inset-px rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300
                       bg-gradient-to-r ${colorClasses.glowFrom} ${colorClasses.glowTo}`}>
        </div>

        <div className="relative z-10">
            <div className="flex justify-between items-start">
                <p className="text-sm font-medium text-gray-400">{title}</p>
                <span className={`text-xl ${colorClasses.text}`}>{icon}</span>
            </div>
            <p className="text-4xl font-bold text-white mt-2">{value}</p>
        </div>
    </div>
);


// ===================================================================================
// C. Main Overview Dashboard Component
// Fetches data and lays out the Welcome banner and Stat Cards in a responsive grid.
// ===================================================================================
export function OverviewDashboard() {
    const { auth } = useAuth();
    const [stats, setStats] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const statCardsConfig = [
        { key: 'users', title: "Total Agents & Admins", icon: <FaUsers />, colorClasses: { border: 'border-cyan-400', text: 'text-cyan-400', glowFrom: 'from-cyan-500', glowTo: 'to-cyan-900' } },
        { key: 'logs', title: "Total Conversations", icon: <FaComments />, colorClasses: { border: 'border-purple-400', text: 'text-purple-400', glowFrom: 'from-purple-500', glowTo: 'to-purple-900' } },
        { key: 'intents', title: "Trained AI Intents", icon: <FaBrain />, colorClasses: { border: 'border-teal-400', text: 'text-teal-400', glowFrom: 'from-teal-500', glowTo: 'to-teal-900' } },
        { key: 'contacts', title: "Contact Submissions", icon: <FaEnvelope />, colorClasses: { border: 'border-indigo-400', text: 'text-indigo-400', glowFrom: 'from-indigo-500', glowTo: 'to-indigo-900' } },
        { key: 'collaborates', title: "Collaborate Requests", icon: <FaHandshake />, colorClasses: { border: 'border-yellow-400', text: 'text-yellow-400', glowFrom: 'from-yellow-500', glowTo: 'to-yellow-900' } },
        { key: 'demos', title: "Demo Requests", icon: <FaFlask />, colorClasses: { border: 'border-pink-400', text: 'text-pink-400', glowFrom: 'from-pink-500', glowTo: 'to-pink-900' } },
    ];

    useEffect(() => {
        const fetchStats = async () => {
            if (!auth?.token) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const [ usersRes, logsRes, intentsRes, whatsappRes, contactRes, collaborateRes, demoRes ] = await Promise.all([
                    axios.get('/api/superadmin/users'),
                    axios.get('/api/chat-history'),
                    axios.get('/api/superadmin/ai-entries'),
                    axios.get('/api/superadmin/whatsapp/status'),
                    axios.get('/api/contact'),
                    axios.get('/api/collaborate'),
                    axios.get('/api/request-demo')
                ]);

                setStats({
                    users: usersRes.data.length,
                    logs: logsRes.data.length,
                    intents: intentsRes.data.length,
                    whatsapp: whatsappRes.data.status,
                    contacts: contactRes.data.length,
                    collaborates: collaborateRes.data.length,
                    demos: demoRes.data.length
                });
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
                const errorState = { users: 'N/A', logs: 'N/A', intents: 'N/A', whatsapp: 'Error', contacts: 'N/A', collaborates: 'N/A', demos: 'N/A' };
                setStats(errorState);
            } finally {
                // Add a small delay to prevent jarring flash of content
                setTimeout(() => setIsLoading(false), 500);
            }
        };

        fetchStats();
    }, [auth]);

    // Handle the dynamic WhatsApp card separately
    const whatsappStatus = stats.whatsapp || '...';
    const isWhatsappConnected = whatsappStatus.toLowerCase() === 'connected';
    const whatsappCardConfig = { 
        title: "WhatsApp Bot Status", 
        icon: <FaWhatsapp />, 
        value: <span className="capitalize">{whatsappStatus}</span>,
        colorClasses: isWhatsappConnected 
            ? { border: 'border-green-400', text: 'text-green-400', glowFrom: 'from-green-500', glowTo: 'to-green-900' }
            : { border: 'border-red-500', text: 'text-red-500', glowFrom: 'from-red-600', glowTo: 'to-red-900' }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            
            {/* Welcome Banner */}
            <div className="md:col-span-2 lg:col-span-3 xl:col-span-4 bg-gray-800/50 border border-gray-700 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-2">Welcome back, Super Administrator</h2>
                <p className="text-gray-400">
                    This is your central command for the Void Platform. All system metrics and management tools are at your disposal.
                </p>
            </div>

            {/* Conditional Rendering: Skeletons or Stat Cards */}
            {isLoading 
                ? (
                    // Show 7 skeleton loaders
                    [...Array(7)].map((_, index) => <StatCardSkeleton key={index} />)
                ) : (
                    <>
                        {/* Map over the config to render standard stat cards */}
                        {statCardsConfig.map(card => (
                            <StatCard
                                key={card.key}
                                icon={card.icon}
                                title={card.title}
                                value={stats[card.key] === 'N/A' ? <FaExclamationCircle className="text-red-500" /> : stats[card.key]}
                                colorClasses={card.colorClasses}
                            />
                        ))}
                        {/* Render the special WhatsApp card */}
                        <StatCard
                            icon={whatsappCardConfig.icon}
                            title={whatsappCardConfig.title}
                            value={whatsappCardConfig.value}
                            colorClasses={whatsappCardConfig.colorClasses}
                        />
                    </>
                )
            }
        </div>
    );
}